/**
 * Season settings dialog with two uses:
 *   - kind "season": create or edit a season. A new season starts from the
 *     venue season defaults. The defaults load again when the host picks
 *     another venue.
 *   - kind "venueDefaults": edit the season defaults of one venue. New
 *     seasons at that venue start from these values.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  Flex,
  RadioGroup,
  Text,
  TextField,
} from '@radix-ui/themes';
import VenuePicker from '../Teams/VenuePicker';
import type { VenueOption } from '../Teams/types';
import {
  activeTiebreakers,
  describeRule,
  endOfDayIso,
  parsePointsTable,
  startOfDayIso,
  toDayInput,
  type LeftEarlyRule,
  type Tiebreaker,
} from '../../utils/seasons';
import TiebreakerEditor from './TiebreakerEditor';
import {
  createSeason,
  errorMessage,
  fetchVenueSeasonDefaults,
  saveVenueSeasonDefaults,
  updateSeason,
  type ScoringMode,
  type Season,
  type SeasonInput,
  type SeasonRuleInput,
} from './seasonsApi';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venues: VenueOption[];
  defaultVenueId: number | null;
  kind?: 'season' | 'venueDefaults';
  // kind "season" only. When set, the dialog edits this season.
  season?: Season | null;
  onSaved: (season: Season | null) => void;
}

type Criterion = Exclude<Tiebreaker, 'NONE'>;

const SeasonForm: React.FC<Props> = ({
  open,
  onOpenChange,
  venues,
  defaultVenueId,
  kind = 'season',
  season,
  onSaved,
}) => {
  const isDefaults = kind === 'venueDefaults';
  const [name, setName] = useState('');
  const [venueId, setVenueId] = useState<number | null>(defaultVenueId);
  const [startDay, setStartDay] = useState('');
  const [endDay, setEndDay] = useState('');
  const [mode, setMode] = useState<ScoringMode>('TOTAL_POINTS');
  const [tableText, setTableText] = useState('');
  const [useBest, setUseBest] = useState(false);
  const [bestNights, setBestNights] = useState('8');
  const [bonus, setBonus] = useState('0');
  const [leftEarly, setLeftEarly] = useState<LeftEarlyRule>('KEEP_POINTS');
  const [tiebreakers, setTiebreakers] = useState<Criterion[]>([]);
  const [isPublic, setIsPublic] = useState(true);
  const [defaultsNote, setDefaultsNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyRule = (r: SeasonRuleInput) => {
    setMode(r.scoringMode);
    setTableText(r.placementPoints.join(', '));
    setUseBest(Boolean(r.bestNights));
    setBestNights(String(r.bestNights || 8));
    setBonus(String(r.attendanceBonus ?? 0));
    setLeftEarly(r.leftEarly);
    setTiebreakers(activeTiebreakers(r.tiebreakers));
  };

  // Reset the plain fields each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setName(season?.name ?? '');
    setVenueId(season?.venueId ?? defaultVenueId);
    setStartDay(toDayInput(season?.startDate));
    setEndDay(toDayInput(season?.endDate));
    setIsPublic(season?.isPublic ?? true);
    setDefaultsNote(null);
    if (season) applyRule(season);
  }, [open, season, defaultVenueId]);

  // A new season, and the venue defaults editor, load the venue defaults.
  useEffect(() => {
    if (!open || season || venueId === null) return;
    let active = true;
    fetchVenueSeasonDefaults(venueId)
      .then((d) => {
        if (!active) return;
        applyRule(d.values);
        const fromVenue = Object.values(d.source).some((s) => s === 'venue');
        setDefaultsNote(
          fromVenue
            ? `Starts from the season defaults for ${d.venue.name}.`
            : `${d.venue.name} has no season defaults, so the built-in defaults apply.`,
        );
      })
      .catch(() => {
        if (active) setDefaultsNote(null);
      });
    return () => {
      active = false;
    };
  }, [open, season, venueId]);

  const table = parsePointsTable(tableText);
  const bestN = Number(bestNights);
  const bonusN = Number(bonus);

  const problem = (() => {
    if (!isDefaults && !name.trim()) return 'Enter a season name.';
    if (venueId === null) return 'Select a venue.';
    if (!isDefaults && (!startDay || !endDay)) return 'Select the first and last day.';
    if (!isDefaults && endDay < startDay) return 'The last day must be on or after the first day.';
    if (mode === 'PLACEMENT_POINTS' && (!table || table.length === 0)) {
      return 'Enter the placement points as whole numbers, for example 10, 8, 6.';
    }
    if (table === null) return 'Placement points must be whole numbers of 0 or more.';
    if (useBest && (!Number.isInteger(bestN) || bestN < 1)) return 'Best nights must be 1 or more.';
    if (!Number.isInteger(bonusN) || bonusN < 0) return 'Attendance bonus must be 0 or more.';
    return null;
  })();

  const rule: SeasonRuleInput = {
    scoringMode: mode,
    placementPoints: table ?? [],
    bestNights: useBest && bestN > 0 ? bestN : null,
    attendanceBonus: Number.isInteger(bonusN) ? bonusN : 0,
    leftEarly,
    // An empty list means shared rank. The API stores it as [NONE].
    tiebreakers: tiebreakers.length ? tiebreakers : ['NONE'],
  };

  const handleSave = async () => {
    if (problem || venueId === null) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isDefaults) {
        await saveVenueSeasonDefaults(venueId, rule);
        onSaved(null);
      } else {
        const input: SeasonInput = {
          ...rule,
          name: name.trim(),
          venueId,
          startDate: startOfDayIso(startDay),
          endDate: endOfDayIso(endDay),
          isPublic,
        };
        onSaved(season ? await updateSeason(season.id, input) : await createSeason(input));
      }
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, isDefaults ? 'The venue defaults could not be saved.' : 'The season could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  // Clear every venue default, so the built-in defaults apply again.
  const handleResetDefaults = async () => {
    if (venueId === null) return;
    setSaving(true);
    setError(null);
    try {
      const d = await saveVenueSeasonDefaults(venueId, {
        scoringMode: null,
        placementPoints: null,
        bestNights: null,
        attendanceBonus: null,
        leftEarly: null,
        tiebreakers: null,
      });
      applyRule(d.values);
      onSaved(null);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, 'The venue defaults could not be reset.'));
    } finally {
      setSaving(false);
    }
  };

  const venueName = venues.find((v) => v.id === venueId)?.name ?? 'this venue';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="560px">
        <Dialog.Title>
          {isDefaults ? `Season defaults for ${venueName}` : season ? 'Edit season' : 'New season'}
        </Dialog.Title>
        <Dialog.Description size="2" mb="3" color="gray">
          {isDefaults
            ? 'A new season at this venue starts from these settings. Existing seasons do not change.'
            : 'Every game night at the venue from the first day to the last day counts.'}
        </Dialog.Description>

        <Flex direction="column" gap="3">
          {!isDefaults && (
            <label>
              <Text as="div" size="2" weight="medium" mb="1">Name</Text>
              <TextField.Root placeholder="Fall 2026" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </label>
          )}

          {(!isDefaults || venues.length > 1) && (
            <div>
              <Text as="div" size="2" weight="medium" mb="1">Venue</Text>
              <VenuePicker venues={venues} value={venueId} onChange={setVenueId} />
            </div>
          )}

          {!season && defaultsNote && !isDefaults && (
            <Text size="1" color="gray">{defaultsNote}</Text>
          )}

          {!isDefaults && (
            <Flex gap="3" wrap="wrap">
              <label style={{ flex: 1, minWidth: 160 }}>
                <Text as="div" size="2" weight="medium" mb="1">First day</Text>
                <TextField.Root type="date" value={startDay} onChange={(e) => setStartDay(e.target.value)} />
              </label>
              <label style={{ flex: 1, minWidth: 160 }}>
                <Text as="div" size="2" weight="medium" mb="1">Last day</Text>
                <TextField.Root type="date" value={endDay} onChange={(e) => setEndDay(e.target.value)} />
              </label>
            </Flex>
          )}

          <div>
            <Text as="div" size="2" weight="medium" mb="1">Scoring</Text>
            <RadioGroup.Root value={mode} onValueChange={(v) => setMode(v as ScoringMode)}>
              <RadioGroup.Item value="TOTAL_POINTS">Total points: each night adds the team score</RadioGroup.Item>
              <RadioGroup.Item value="PLACEMENT_POINTS">Placement points: each night adds points for the place</RadioGroup.Item>
            </RadioGroup.Root>
          </div>

          {mode === 'PLACEMENT_POINTS' && (
            <label>
              <Text as="div" size="2" weight="medium" mb="1">Points for 1st, 2nd, 3rd and so on</Text>
              <TextField.Root value={tableText} onChange={(e) => setTableText(e.target.value)} />
              <Text as="div" size="1" color="gray" mt="1">
                Places past the end of the list get 0. Tied teams each get the points for the shared place.
              </Text>
            </label>
          )}

          <Flex align="center" gap="2" wrap="wrap">
            <Text as="label" size="2">
              <Flex gap="2" align="center">
                <Checkbox checked={useBest} onCheckedChange={(c) => setUseBest(c === true)} />
                Count only the best
              </Flex>
            </Text>
            <TextField.Root
              type="number"
              min={1}
              style={{ width: 80 }}
              value={bestNights}
              disabled={!useBest}
              onChange={(e) => setBestNights(e.target.value)}
              aria-label="Best nights"
            />
            <Text size="2">nights per team</Text>
          </Flex>

          <Flex align="center" gap="2" wrap="wrap">
            <Text size="2">Attendance bonus:</Text>
            <TextField.Root
              type="number"
              min={0}
              style={{ width: 80 }}
              value={bonus}
              onChange={(e) => setBonus(e.target.value)}
              aria-label="Attendance bonus"
            />
            <Text size="2">points per night attended</Text>
          </Flex>

          <div>
            <Text as="div" size="2" weight="medium" mb="1">A team that leaves early</Text>
            <RadioGroup.Root value={leftEarly} onValueChange={(v) => setLeftEarly(v as LeftEarlyRule)}>
              <RadioGroup.Item value="KEEP_POINTS">Keeps the points it earned that night</RadioGroup.Item>
              <RadioGroup.Item value="ZERO">Scores 0 for that night</RadioGroup.Item>
            </RadioGroup.Root>
            <Text as="div" size="1" color="gray" mt="1">
              The attendance bonus counts in both cases.
              {mode === 'PLACEMENT_POINTS' && leftEarly === 'KEEP_POINTS' &&
                ' With placement points, the team gets a place from its score when it left. The other teams keep their places.'}
            </Text>
          </div>

          <div>
            <Text as="div" size="2" weight="medium" mb="1">Tiebreakers for equal season points</Text>
            <TiebreakerEditor value={tiebreakers} onChange={setTiebreakers} />
          </div>

          {!isDefaults && (
            <Text as="label" size="2">
              <Flex gap="2" align="center">
                <Checkbox checked={isPublic} onCheckedChange={(c) => setIsPublic(c === true)} />
                Show the standings on the public site
              </Flex>
            </Text>
          )}

          <Text size="1" color="gray">Rule: {describeRule(rule)}</Text>

          {error && (
            <Callout.Root color="red" size="1">
              <Callout.Text>{error}</Callout.Text>
            </Callout.Root>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="between" wrap="wrap">
          <div>
            {isDefaults && (
              <Button variant="ghost" color="gray" onClick={handleResetDefaults} disabled={saving}>
                Reset to built-in defaults
              </Button>
            )}
          </div>
          <Flex gap="3">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button onClick={handleSave} disabled={saving || Boolean(problem)}>
              {saving ? 'Saving…' : isDefaults ? 'Save defaults' : season ? 'Save' : 'Create season'}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default SeasonForm;
